import { Flex, Text, Button, Loader, Block, Textarea } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useState } from 'react';
import logError from 'Helpers/logError';
import { dateCalendar } from 'App/helpers/DateHelpers';

import './OrderForecast.scss';

type Signal = { signal: string; source: string; severity?: string };
type Action = { action: string; priority: string; reasoning: string };

type ForecastData = {
	forecastId: number | null;
	deal: {
		id: number;
		description: string;
		value: number;
		currency: string;
		stage: string;
		client: { name: string };
	};
	forecast: {
		status: string;
		confidence: string;
		summary: string | string[];
		depth: string;
		analyzedAt: string;
		closingDateAssessment: { likelihood: string; estimatedSlippageDays: number; reasoning: string };
		positiveSignals: Signal[];
		riskSignals: Signal[];
		communicationInsights: {
			pricingConcerns: boolean;
			timelineAlignment: string;
			customerEngagement: string;
			keyTopics: string[];
			overallTone: string;
		};
		stakeholderAssessment: { adequate: boolean; missingRoles: string[]; suggestedActions: string[] };
		icpMatch?: { match: string; reasoning: string };
		recommendedActions: Action[];
	};
};

const statusLabel: Record<string, string> = {
	on_track: 'On Track',
	needs_attention: 'Needs Attention',
	at_risk: 'At Risk',
	stalled: 'Stalled'
};

// Format snake_case enum values for display
const formatEnum = (value: string): string => {
	const str = value.replace(/_/g, ' ');
	return str.charAt(0).toUpperCase() + str.slice(1);
};

const classes = new BemClass('OrderForecast');

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<div className={classes.elem('section').b()}>
		<div className={classes.elem('section-title').b()}>{title}</div>
		{children}
	</div>
);

const BulletList = ({
	items,
	mod
}: {
	items: Array<{ text: string; sub?: string; bulletMod?: string }>;
	mod?: string;
}) => (
	<Flex direction="column" gap="u1">
		{items.map((item, i) => (
			<Flex key={i} gap="u2" alignItems="flex-start" className={classes.elem('signal').b()}>
				<div
					className={classes
						.elem('bullet')
						.mod({ [item.bulletMod || mod || 'default']: true })
						.b()}
				/>
				<Flex direction="column">
					<Text size="sm">{item.text}</Text>
					{item.sub && (
						<Text size="sm" color="grey-10">
							{item.sub}
						</Text>
					)}
				</Flex>
			</Flex>
		))}
	</Flex>
);

const FeedbackBar = ({ forecastId, orderId }: { forecastId: number | null; orderId: number }) => {
	const { t } = useTranslation();
	const [submitted, setSubmitted] = useState(false);
	const [showComment, setShowComment] = useState(false);
	const [comment, setComment] = useState('');
	const [rating, setRating] = useState(2);

	if (!forecastId || submitted) {
		return submitted ? (
			<Text size="sm" color="grey-10">
				{t('ai.dealForecast.feedback.thanks')}
			</Text>
		) : null;
	}

	const submit = async (rating: number) => {
		try {
			await fetch(`/api/v2/ai/deal-forecast/${forecastId}/feedback`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ rating, comment: comment || undefined, orderId })
			});
			setSubmitted(true);
		} catch {
			// silent fail
		}
	};

	return (
		<Flex direction="column" gap="u2">
			<Flex alignItems="center" gap="u2">
				<Text size="sm" color="grey-10">
					{t('ai.dealForecast.feedback.useful')}
				</Text>
				<Text
					size="sm"
					bold={showComment && rating === 2}
					color={showComment && rating === 2 ? undefined : 'grey-10'}
					onClick={() => {
						setRating(2);
						setShowComment(true);
					}}
					style={{ cursor: 'pointer' }}
				>
					{t('ai.dealForecast.feedback.yes')}
				</Text>
				<Text size="sm" color="grey-10">
					{'/'}
				</Text>
				<Text
					size="sm"
					bold={showComment && rating === 1}
					color={showComment && rating === 1 ? undefined : 'grey-10'}
					onClick={() => {
						setRating(1);
						setShowComment(true);
					}}
					style={{ cursor: 'pointer' }}
				>
					{t('ai.dealForecast.feedback.no')}
				</Text>
			</Flex>
			{showComment && (
				<Flex direction="column" gap="u2">
					<Textarea
						value={comment}
						onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
						placeholder={t('ai.dealForecast.feedback.placeholder')}
					/>
					<Flex justifyContent="flex-end">
						<Button size="sm" onClick={() => submit(rating)}>
							{t('ai.dealForecast.feedback.send')}
						</Button>
					</Flex>
				</Flex>
			)}
		</Flex>
	);
};

const OrderForecast = ({ orderId }: { orderId: number }) => {
	const { t } = useTranslation();
	const [data, setData] = useState<ForecastData | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchStored = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await fetch(`/api/v2/ai/deal-forecast/${orderId}`);
			if (!response.ok) {
				throw new Error(`Failed to fetch forecast: ${response.status}`);
			}
			const text = await response.text();
			if (!text) return;
			const result = JSON.parse(text);
			const forecast = result.data ?? result;
			if (forecast?.forecast) {
				setData(forecast);
			}
		} catch (err) {
			logError(err, 'OrderForecast');
			setError(err instanceof Error ? err.message : 'Failed to load forecast');
		} finally {
			setLoading(false);
		}
	};

	const runAnalysis = async (depth: 'basic' | 'standard' | 'deep') => {
		try {
			setRefreshing(true);
			setError(null);
			const response = await fetch(`/api/v2/ai/deal-forecast/${orderId}/analyze`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ depth })
			});
			if (!response.ok) {
				throw new Error(`Failed to run analysis: ${response.status}`);
			}
			const result = await response.json();
			setData(result.data ?? result);
		} catch (err) {
			logError(err, 'OrderForecast');
			setError(err instanceof Error ? err.message : 'Failed to run analysis');
		} finally {
			setRefreshing(false);
		}
	};

	useEffect(() => {
		if (orderId) {
			fetchStored();
		}
	}, [orderId]);

	if (loading) {
		return (
			<Flex justifyContent="center" alignItems="center" className={classes.mod('loading').b()}>
				<Loader size="md" />
			</Flex>
		);
	}

	if (error && !data) {
		return (
			<Flex direction="column" alignItems="center" gap="u4" className={classes.mod('empty').b()}>
				<Text color="grey-10">{error}</Text>
				<Button onClick={() => fetchStored()} size="sm">
					{t('ai.dealForecast.retry')}
				</Button>
			</Flex>
		);
	}

	if (!data) {
		return (
			<Flex direction="column" alignItems="center" gap="u4" className={classes.mod('empty').b()}>
				{refreshing ? (
					<>
						<Loader size="md" />
						<Text size="sm" color="grey-10">
							{t('ai.dealForecast.analyzing')}
						</Text>
					</>
				) : (
					<>
						<Text bold>{t('ai.dealForecast.noForecast')}</Text>
						<Text size="sm" color="grey-10">
							{t('ai.dealForecast.empty.subtitle')}
						</Text>
						<Button onClick={() => runAnalysis('deep')} size="sm">
							{t('ai.dealForecast.runAnalysis')}
						</Button>
					</>
				)}
			</Flex>
		);
	}

	const { forecast } = data;
	const analyzedLabel = forecast.analyzedAt ? String(dateCalendar(forecast.analyzedAt, true)) : null;

	return (
		<div className={classes.b()}>
			{/* Header: status + summary */}
			<div className={classes.elem('header').b()}>
				<Flex justifyContent="space-between" alignItems="center">
					<Flex gap="u3" alignItems="center">
						<span className={classes.elem('status').b()}>
							{statusLabel[forecast.status] || forecast.status}
						</span>
						<Text size="sm" color="grey-10">
							{forecast.confidence} {t('ai.dealForecast.confidence')}
						</Text>
					</Flex>
					<Flex gap="u2" alignItems="center">
						<Text size="sm" color="grey-10">
							{`${analyzedLabel ?? ''} · ${forecast.depth}`}
						</Text>
						<Button size="sm" type="link" onClick={() => runAnalysis('deep')} loading={refreshing}>
							{t('ai.dealForecast.reAnalyze')}
						</Button>
					</Flex>
				</Flex>
				<Block space="mtm">
					<BulletList
						items={(Array.isArray(forecast.summary) ? forecast.summary : [forecast.summary]).map(s => ({
							text: s
						}))}
					/>
				</Block>
			</div>

			{/* Closing date */}
			<Section title={t('ai.dealForecast.closingDate')}>
				<Flex gap="u4" alignItems="baseline">
					<Text bold>{formatEnum(forecast.closingDateAssessment.likelihood)}</Text>
					{forecast.closingDateAssessment.estimatedSlippageDays > 0 && (
						<Text size="sm" color="grey-10">
							{`+${forecast.closingDateAssessment.estimatedSlippageDays}d est. slippage`}
						</Text>
					)}
				</Flex>
				<Text size="sm" color="grey-10">
					{forecast.closingDateAssessment.reasoning}
				</Text>
			</Section>

			{/* Risk signals */}
			{forecast.riskSignals.length > 0 && (
				<Section title={t('ai.dealForecast.risks')}>
					<BulletList
						items={forecast.riskSignals.map(s => ({
							text: s.signal,
							bulletMod: `risk-${s.severity || 'medium'}`
						}))}
					/>
				</Section>
			)}

			{/* Positive signals */}
			{forecast.positiveSignals.length > 0 && (
				<Section title={t('ai.dealForecast.positiveSignals')}>
					<BulletList
						items={forecast.positiveSignals.map(s => ({
							text: s.signal,
							bulletMod: 'positive'
						}))}
					/>
				</Section>
			)}

			{/* Recommended actions */}
			{forecast.recommendedActions.length > 0 && (
				<Section title={t('ai.dealForecast.actions')}>
					<BulletList
						items={forecast.recommendedActions.map(a => ({
							text: a.action,
							sub: a.reasoning,
							bulletMod: `priority-${a.priority}`
						}))}
					/>
				</Section>
			)}

			{/* Stakeholders */}
			{!forecast.stakeholderAssessment.adequate && (
				<Section title={t('ai.dealForecast.stakeholderGaps')}>
					{forecast.stakeholderAssessment.missingRoles.length > 0 && (
						<Text size="sm">
							{t('ai.dealForecast.missing')} {forecast.stakeholderAssessment.missingRoles.join(', ')}
						</Text>
					)}
					{forecast.stakeholderAssessment.suggestedActions.length > 0 && (
						<BulletList items={forecast.stakeholderAssessment.suggestedActions.map(a => ({ text: a }))} />
					)}
				</Section>
			)}

			{/* Communication */}
			<Section title={t('ai.dealForecast.communication')}>
				<div className={classes.elem('meta-grid').b()}>
					<Flex direction="column" gap="u1">
						<span className={classes.elem('meta-label').b()}>
							<Text size="sm" color="grey-10">
								{t('ai.dealForecast.engagement')}
							</Text>
						</span>
						<Text size="sm" bold>
							{formatEnum(forecast.communicationInsights.customerEngagement)}
						</Text>
					</Flex>
					<Flex direction="column" gap="u1">
						<span className={classes.elem('meta-label').b()}>
							<Text size="sm" color="grey-10">
								{t('ai.dealForecast.timeline')}
							</Text>
						</span>
						<Text size="sm" bold>
							{formatEnum(forecast.communicationInsights.timelineAlignment)}
						</Text>
					</Flex>
					<Flex direction="column" gap="u1">
						<span className={classes.elem('meta-label').b()}>
							<Text size="sm" color="grey-10">
								{t('ai.dealForecast.pricingConcerns')}
							</Text>
						</span>
						<Text size="sm" bold>
							{forecast.communicationInsights.pricingConcerns
								? t('ai.dealForecast.feedback.yes')
								: t('ai.dealForecast.feedback.no')}
						</Text>
					</Flex>
				</div>
			</Section>

			{/* ICP match */}
			{forecast.icpMatch && (
				<Section title={t('ai.dealForecast.icpMatch')}>
					<Text size="sm" bold>
						{formatEnum(forecast.icpMatch.match)}
					</Text>
					<Text size="sm" color="grey-10">
						{forecast.icpMatch.reasoning}
					</Text>
				</Section>
			)}

			{/* Feedback */}
			<div className={classes.elem('section').b()}>
				<FeedbackBar forecastId={data.forecastId} orderId={orderId} />
			</div>
		</div>
	);
};

export default OrderForecast;
